// University of New Hampshire: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNH's Common Data Set 2025-2026 (Institutional Research, published
//    9.2.26): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy and score date, C21/C22 (early action,
//    no early decision), G1/G5 costs for 2026-2027 (section G is not marked
//    as unavailable). Figures read from the PDF's filled form fields.
//  - admissions.unh.edu: First-Year Students (application deadlines and
//    decision timing) and Test-Optional Policy.
//  - College Scorecard API, school id 183044 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UNH Common Data Set 2025-2026',
        url: 'https://www.unh.edu/institutional-research/sites/default/files/media/2026-09/CDS%202025-2026%20UNH%20Published%209.2.26.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UNH Admissions, First-Year Students',
        url: 'https://admissions.unh.edu/apply/first-year',
        read: '2026-10-01',
    },
    testing: {
        label: 'UNH Admissions, Test-Optional Policy',
        url: 'https://admissions.unh.edu/apply/test-optional',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?183044-University-of-New-Hampshire-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-new-hampshire',
    kbId: 'university_of_new_hampshire',
    name: 'University of New Hampshire',
    shortName: 'UNH',
    location: 'Durham, NH',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UNH marketing); decoration only.
    branding: {
        primary: '#003591',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_new_hampshire_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 21567,
        admitted: 19224,
        enrolled: 2302,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4548, admitted: 3939 },
            { group: 'Out-of-state', applied: 16521, admitted: 14860 },
            { group: 'International', applied: 498, admitted: 425 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNH\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UNH has no early decision plan. Its early action plan is not restrictive, and the Common Data Set gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'A few majors have an earlier priority deadline:',
        rows: [
            { school: 'Exercise Science, Health Sciences, Nursing, Occupational Therapy, and Health and Human Services undeclared', requirement: 'Apply by December 1.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: 'By the end of January', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By the end of March', binding: false },
        ],
        note: 'For Early Action, the Common App must arrive by November 15 and supporting documents by December 12; an incomplete early application moves to Regular Decision. UNH also lists a Regular Decision extension to February 20. Students admitted early have until May 1 to enroll. The financial aid priority date is March 1.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UNH is test-optional: SAT and ACT scores are not required. On your application you answer whether you want your scores considered; if you do, they must come directly from the testing agency. Students who enroll and have taken a test are asked for scores after May 1, for research only.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [16304, 37996] },
            { item: 'Required fees', amounts: [3868, 3868] },
            { item: 'Housing (on campus)', amounts: [9410, 9410] },
            { item: 'Food (on-campus meal plan)', amounts: [5624, 5624] },
            { item: 'Books and supplies', amounts: [1000, 1000] },
            { item: 'Transportation', amounts: [300, 300] },
            { item: 'Other expenses', amounts: [3796, 3796] },
        ],
        totals: [40302, 61994],
        note: 'For a full-time undergraduate living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 26150,
            byIncome: [
                { income: '$0 to $30,000', amount: 18960 },
                { income: '$30,001 to $48,000', amount: 19732 },
                { income: '$48,001 to $75,000', amount: 20270 },
                { income: '$75,001 to $110,000', amount: 22775 },
                { income: 'Over $110,000', amount: 30808 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UNH\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
